import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { getThingOperationByType, putRearmeEvento } from '../../../../services';
import { notifyError, notifySuccess } from '../../../../utils';
import { dateFormats } from '../../../../constants';
import AlarmAnalytics from './alarm-analytics';
import * as s from './styled-machine-log';

// MachineLog — aba "Log" do detalhe operacional. Lista cronologica
// (mais recente no topo) dos eventos que tiraram a maquina do normal:
// alarmes, bloqueios e trips. Identifica a causa (variavel que disparou),
// a severidade, recencia e reincidencia. Segue a maquina selecionada no
// carrossel da aba Detalhes (prop thingId).
//
// Fonte: getThingOperationByType(thingId, 'all') — mesmo endpoint usado
// pela tela Operacional (notification.js). Cada item traz:
//   { insertdate, variable, variableRelation: { name, unit, type:{name} } }
// onde type.name e 'alarm' | 'blockade' | 'trip'.

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_COLLAPSED = 10;

// Mapeia o tipo do backend pra severidade visual. Por decisao do produto
// alarme, bloqueio e trip sao todos criticos (vermelho); o que distingue
// e o label + icone. Tokens CCO.
const severityFor = (typeName) => {
  const t = (typeName || '').toLowerCase();
  if (t === 'trip')
    return { key: 'trip', label: 'Trip', color: 'var(--cco-red)', icon: 'zap' };
  if (t === 'blockade' || t === 'bloqueio')
    return {
      key: 'block',
      label: 'Bloqueio',
      color: 'var(--cco-red)',
      icon: 'ban',
    };
  if (t === 'alarm' || t === 'alarme')
    return {
      key: 'alarm',
      label: 'Alarme',
      color: 'var(--cco-red)',
      icon: 'alert',
    };
  return {
    key: 'event',
    label: 'Evento',
    color: 'var(--cco-ink-mute)',
    icon: 'alert',
  };
};

// Filtros (chips) — chave casa com severityFor().key. 'all' = sem filtro.
const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'alarm', label: 'Alarmes' },
  { key: 'block', label: 'Bloqueios' },
  { key: 'trip', label: 'Trips' },
];

// Tempo relativo curto ("agora", "ha 12 min", "ha 3h", "ha 2d"). Da ao
// operador a nocao de recencia sem ele precisar calcular do timestamp.
const relativeTime = (ts) => {
  const diff = Date.now() - ts;
  if (diff < 0) return 'agora';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
};

// Rotulo do divisor de dia: "Hoje" / "Ontem" / "dd/MM/yyyy". Compara so a
// data (zera horas) pra nao depender da hora do evento.
const dayLabelFor = (ts) => {
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const today = startOfDay(Date.now());
  const evtDay = startOfDay(ts);
  if (evtDay === today) return 'Hoje';
  if (evtDay === today - DAY_MS) return 'Ontem';
  return format(new Date(ts), 'dd/MM/yyyy');
};

// #7 Formata o limite configurado (range) de forma compacta. O VALOR
// medido no instante do evento nao existe no banco — exibimos so o
// threshold pra contexto ("acima de 90 °C"). Retorna null quando nao ha
// limite cadastrado.
const thresholdLabel = (range, unit) => {
  if (!range) return null;
  const u = unit ? ` ${unit}` : '';
  const hasMax = range.max !== null && range.max !== undefined && range.max !== '';
  const hasMin = range.min !== null && range.min !== undefined && range.min !== '';
  if (hasMax && hasMin) return `limite ${range.min}–${range.max}${u}`;
  if (hasMax) return `limite ≤ ${range.max}${u}`;
  if (hasMin) return `limite ≥ ${range.min}${u}`;
  return null;
};

// Icones line-style (Lucide) inline — sem dependencia npm, stroke
// currentColor herdando a cor de severidade do wrapper.
const LogGlyph = ({ name }) => {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  if (name === 'zap')
    return (
      <svg {...common}>
        <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    );
  if (name === 'ban')
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="m4.9 4.9 14.2 14.2" />
      </svg>
    );
  // alert-triangle (default)
  return (
    <svg {...common}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
};

const ClockGlyph = () => (
  <svg
    width="34"
    height="34"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

// onEventClick (opcional): chamado ao clicar num evento. Plugue aqui a
// correlacao com o grafico (marcar o instante do evento na curva).
const MachineLog = ({ thingId, onEventClick }) => {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(false);
  // Rearme e acao com efeito no equipamento — exige confirmacao em dois
  // passos (clique arma "Confirmar", segundo clique dispara). 'rearming'
  // bloqueia a UI durante o request.
  const [confirmRearm, setConfirmRearm] = useState(false);
  const [rearming, setRearming] = useState(false);

  // load() isolado pra ser reusado pelo rearme (refetch apos rearmar) sem
  // duplicar a logica de normalizacao.
  const load = useCallback(
    (signal) => {
      if (!thingId) return Promise.resolve();
      setLoading(true);
      return getThingOperationByType(thingId, 'all')
        .then((resp) => {
          if (signal && signal.cancelled) return;
          setRaw(Array.isArray(resp && resp.data) ? resp.data : []);
        })
        .catch(() => {
          // Silencioso: endpoint pode falhar pra maquinas sem historico.
          // UI cai no estado vazio em vez de poluir o notification.
          if (!(signal && signal.cancelled)) setRaw([]);
        })
        .finally(() => {
          if (!(signal && signal.cancelled)) setLoading(false);
        });
    },
    [thingId]
  );

  useEffect(() => {
    const signal = { cancelled: false };
    // Reset de filtro/expandido/confirmacao ao trocar de maquina.
    setFilter('all');
    setExpanded(false);
    setConfirmRearm(false);
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  // Rearma todos os alarmes da maquina (acao machine-level — o endpoint
  // rearma a thing inteira, nao um evento isolado). Refetch ao concluir
  // pra refletir o estado novo.
  const handleRearm = useCallback(() => {
    if (rearming) return;
    setRearming(true);
    putRearmeEvento({ thingId: parseInt(thingId, 10) })
      .then(() => {
        notifySuccess('Rearme enviado à máquina');
        setConfirmRearm(false);
        return load();
      })
      .catch(() => notifyError('Falha ao rearmar a máquina'))
      .finally(() => setRearming(false));
  }, [thingId, rearming, load]);

  // Normaliza + ordena (mais recente primeiro). insertdate vem como ISO
  // string; convertemos pra epoch pra ordenar e formatar. cause usa o
  // nome amigavel da variavel; ihm (chave crua) e usado pra contar
  // reincidencia (variavel que repete).
  const events = useMemo(() => {
    return raw
      .map((item, idx) => {
        const ts =
          item && item.insertdate ? new Date(item.insertdate).getTime() : null;
        const vr = (item && item.variableRelation) || {};
        const range = (item && item.range) || null;
        return {
          id: item && item.id != null ? item.id : `evt-${idx}`,
          ts,
          typeName: vr.type && vr.type.name,
          cause: vr.name || (item && item.variable) || 'Evento',
          ihm: (item && item.variable) || (vr && vr.ihmname) || '',
          unit: vr.unit || (range && range.unit) || '',
          range,
          hasRearm: !!(item && item.hasRearm),
        };
      })
      .filter((e) => Number.isFinite(e.ts))
      .sort((a, b) => b.ts - a.ts);
  }, [raw]);

  // #9 Rearme so faz sentido se ha pelo menos um alarme rearmavel.
  const canRearm = useMemo(() => events.some((e) => e.hasRearm), [events]);

  // Contagem por severidade pros chips de filtro (#1).
  const counts = useMemo(() => {
    const c = { all: events.length, alarm: 0, block: 0, trip: 0 };
    events.forEach((e) => {
      const k = severityFor(e.typeName).key;
      if (c[k] != null) c[k] += 1;
    });
    return c;
  }, [events]);

  // Reincidencia (#3): quantas vezes cada variavel (ihm) aparece na janela.
  // Anexada a cada evento pra exibir badge "xN" quando > 1.
  const recurrence = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const key = e.ihm || e.cause;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [events]);

  // Top ofensor (#4): variavel que mais disparou na janela (>= 2). Da ao
  // operador o "o que mais incomoda" sem percorrer a lista.
  const topOffender = useMemo(() => {
    let best = null;
    Object.keys(recurrence).forEach((key) => {
      if (!best || recurrence[key] > best.count) {
        const sample = events.find((e) => (e.ihm || e.cause) === key);
        best = { key, count: recurrence[key], cause: sample && sample.cause };
      }
    });
    return best && best.count >= 2 ? best : null;
  }, [recurrence, events]);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => severityFor(e.typeName).key === filter);
  }, [events, filter]);

  const visible = expanded ? filtered : filtered.slice(0, MAX_COLLAPSED);
  const hiddenCount = filtered.length - visible.length;

  if (loading) {
    return (
      <s.LogContainer>
        <s.LogState>Carregando eventos…</s.LogState>
      </s.LogContainer>
    );
  }

  if (!events.length) {
    return (
      <s.LogContainer>
        <s.LogState>
          <ClockGlyph />
          Nenhum evento registrado para esta máquina.
        </s.LogState>
      </s.LogContainer>
    );
  }

  // Renderiza a lista com divisores de dia (#2) intercalados quando o dia
  // muda em relacao ao item anterior visivel.
  let lastDay = null;

  return (
    <s.LogContainer>
      {/* Analitica de alarmes: downtime + MTBF/MTTR + Pareto por tempo
          parado (endpoint dedicado). Acima da lista cronologica. */}
      {thingId != null && <AlarmAnalytics thingId={thingId} />}

      {/* #4 Top ofensor — resumo do que mais disparou na janela */}
      {topOffender && (
        <s.TopOffender>
          <span className="lbl">Causa mais frequente</span>
          <span className="cause" title={topOffender.cause}>
            {topOffender.cause}
          </span>
          <span className="count">{topOffender.count}×</span>
        </s.TopOffender>
      )}

      {/* #1 Chips de filtro + #9 acao de rearme (machine-level) */}
      <s.FilterRow>
        {FILTERS.map((f) => {
          const n = counts[f.key] || 0;
          const disabled = f.key !== 'all' && n === 0;
          return (
            <s.FilterChip
              key={f.key}
              type="button"
              $active={filter === f.key}
              disabled={disabled}
              onClick={() => !disabled && setFilter(f.key)}
            >
              {f.label}
              <span className="n">{n}</span>
            </s.FilterChip>
          );
        })}

        {canRearm && (
          <s.RearmWrap>
            {confirmRearm && !rearming && (
              <s.RearmHint>Rearmar a máquina?</s.RearmHint>
            )}
            <s.RearmButton
              type="button"
              $confirm={confirmRearm}
              disabled={rearming}
              onClick={() =>
                confirmRearm ? handleRearm() : setConfirmRearm(true)
              }
            >
              {rearming
                ? 'Rearmando…'
                : confirmRearm
                ? 'Confirmar'
                : 'Rearmar'}
            </s.RearmButton>
            {confirmRearm && !rearming && (
              <s.RearmCancel
                type="button"
                onClick={() => setConfirmRearm(false)}
              >
                Cancelar
              </s.RearmCancel>
            )}
          </s.RearmWrap>
        )}
      </s.FilterRow>

      <s.LogList>
        {visible.map((evt) => {
          const sev = severityFor(evt.typeName);
          const repeats = recurrence[evt.ihm || evt.cause] || 1;
          const day = dayLabelFor(evt.ts);
          const showDivider = day !== lastDay;
          lastDay = day;
          return (
            <React.Fragment key={evt.id}>
              {showDivider && <s.DayDivider>{day}</s.DayDivider>}
              <s.LogItem
                as={onEventClick ? 'button' : 'div'}
                type={onEventClick ? 'button' : undefined}
                $clickable={!!onEventClick}
                onClick={onEventClick ? () => onEventClick(evt) : undefined}
              >
                <s.LogStripe $color={sev.color} />
                <s.LogIcon $color={sev.color}>
                  <LogGlyph name={sev.icon} />
                </s.LogIcon>
                <s.LogBody>
                  <s.LogTop>
                    <s.LogSeverity $color={sev.color}>
                      {sev.label}
                    </s.LogSeverity>
                    <s.LogCause title={evt.cause}>{evt.cause}</s.LogCause>
                    {repeats > 1 && (
                      <s.RecurrenceBadge title={`${repeats} ocorrências na janela`}>
                        {repeats}×
                      </s.RecurrenceBadge>
                    )}
                  </s.LogTop>
                  <s.LogMeta>
                    <span>
                      {format(new Date(evt.ts), dateFormats.DATE_TIME_SLASH)}
                    </span>
                    <s.LogRelative>{relativeTime(evt.ts)}</s.LogRelative>
                    {thresholdLabel(evt.range, evt.unit) && (
                      <s.LogThreshold>
                        {thresholdLabel(evt.range, evt.unit)}
                      </s.LogThreshold>
                    )}
                  </s.LogMeta>
                </s.LogBody>
              </s.LogItem>
            </React.Fragment>
          );
        })}
      </s.LogList>

      {/* #6 Ver todos / ver menos — expande alem dos 10 iniciais */}
      {filtered.length > MAX_COLLAPSED && (
        <s.SeeMore
          type="button"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Ver menos' : `Ver todos (${hiddenCount} restantes)`}
        </s.SeeMore>
      )}

      {!filtered.length && (
        <s.LogState>Nenhum evento deste tipo na janela.</s.LogState>
      )}
    </s.LogContainer>
  );
};

export default MachineLog;
